import type {
  BatchQueryEntry,
  FieldSpec,
  GroupSpec,
  QueryRequest,
  QueryResponse,
  SortSpec,
} from '@moment/horizon-data-access/platform'
import type { QueryClient } from '@tanstack/react-query'
import type { BondBook, QuoteRow } from './quote-books'
import type { SecurityTaxInputs } from './security-tax-expressions'
import type { SecurityRow, SecurityWhere } from './security-types'

import {
  executeSormBatchQuery,
  executeSormQuery,
} from '@moment/horizon-data-access'
import { getEntityContext } from '@moment/sorm-client-data-access/entity-context'
import { sormEntitySchemaQueryOptions } from '@moment/sorm-client-data-access/entity-schema'
import chunk from 'lodash/chunk'

import { createLogger } from '@/lib/logger'

import { planFilterListMembership } from './filter-list-membership'
import {
  buildBooks,
  isQuotePageFull,
  quoteBookBatchEntry,
  quoteIsinChunks,
} from './quote-books'
import {
  canComputeDuration,
  DURATION_COLUMN,
  DURATION_COLUMN_SPEC,
  declareDurationColumn,
} from './security-duration-column'
import { isTaxColumn, taxColumnFieldSpecs } from './security-tax-expressions'
import {
  RISK_GROUP_REQUEST_PARAM,
  SECURITY_ENTITY,
  SECURITY_ID_FIELD,
} from './security-wire-fields'
import { combineAnd, qualifySecurityField } from './translate-filter-object'

const logger = createLogger('shared/sorm/security/fetch-securities')

const QUOTE_ENTRY_PREFIX = '__quotes'

/** SORM's cap on ISINs a single batch entry filters on. */
export const SECURITY_IDS_PER_ENTRY = 1_000

/** What the deployed Security schema serves, read from entity introspection, plus the duration column. */
export interface SecuritySchemaInfo {
  fields: ReadonlySet<string>
  decimalFields: ReadonlySet<string>
}

export async function loadSecuritySchema(
  queryClient: QueryClient,
): Promise<SecuritySchemaInfo> {
  const schema = await queryClient.ensureQueryData(
    sormEntitySchemaQueryOptions(SECURITY_ENTITY),
  )
  const fields = schema.fields ?? []
  const names = new Set(fields.map((field) => field.name))
  const decimalFields = new Set(
    fields.filter((field) => field.type === 'decimal').map((f) => f.name),
  )
  if (canComputeDuration(names)) {
    names.add(DURATION_COLUMN)
    decimalFields.add(DURATION_COLUMN)
  }
  return { fields: names, decimalFields }
}

/**
 * Field specs for the Security fields the deployed schema serves, aliased to
 * their bare names, plus any computed columns. Fields the schema lacks (not
 * yet shipped server-side) are dropped so one missing field cannot fail the
 * whole query. Tax-aware yields (`isTaxColumn`) are computed for `tax`.
 */
export function securityFieldSpecs(
  fields: Iterable<string>,
  schema: SecuritySchemaInfo,
  computed: ReadonlyArray<FieldSpec> = [],
  tax: SecurityTaxInputs = {},
): FieldSpec[] {
  const specs: FieldSpec[] = []
  const taxColumns: string[] = []
  for (const field of new Set([SECURITY_ID_FIELD, ...fields])) {
    if (isTaxColumn(field)) taxColumns.push(field)
    else if (field === DURATION_COLUMN) {
      if (schema.fields.has(field)) specs.push(DURATION_COLUMN_SPEC)
    } else if (schema.fields.has(field)) {
      specs.push({ entity: SECURITY_ENTITY, field, alias: field })
    }
  }
  const taxSpecs = taxColumnFieldSpecs(taxColumns, tax, (field) =>
    schema.fields.has(field),
  )
  return [...specs, ...taxSpecs, ...computed]
}

/** Request params the deployed Security schema reads, with empty values dropped. */
export function securityRequestParams(
  riskGroupId: string | null,
): Record<string, unknown> {
  return riskGroupId ? { [RISK_GROUP_REQUEST_PARAM]: riskGroupId } : {}
}

export interface SecurityQueryInput {
  fields: FieldSpec[]
  where: ReadonlyArray<SecurityWhere>
  requestParams: Record<string, unknown>
  groups?: GroupSpec[]
  sort?: SortSpec[]
  limit?: number
  offset?: number
}

function securityQueryRequest(input: SecurityQueryInput): QueryRequest {
  const { where, requestParams, groups, sort, limit, offset } = input
  return {
    root_entity: SECURITY_ENTITY,
    context: getEntityContext(SECURITY_ENTITY),
    fields: declareDurationColumn(input.fields, input),
    filters: combineAnd([...where]),
    ...(groups && groups.length > 0 ? { groups } : {}),
    ...(sort && sort.length > 0 ? { sort } : {}),
    ...(limit !== undefined ? { limit } : {}),
    ...(offset !== undefined ? { offset } : {}),
    ...(Object.keys(requestParams).length > 0
      ? { request_params: requestParams }
      : {}),
  }
}

export interface SecurityIsinGroup {
  /** Caller's key for this group's rows in the result. */
  key: string
  isins: ReadonlyArray<string>
  fields: FieldSpec[]
  where: ReadonlyArray<SecurityWhere>
  requestParams: Record<string, unknown>
}

// At most SECURITY_IDS_PER_ENTRY ISINs per entry.
function securityIsinBatchEntries(
  groups: ReadonlyArray<SecurityIsinGroup>,
): BatchQueryEntry[] {
  return groups.flatMap((group) =>
    chunk([...new Set(group.isins)], SECURITY_IDS_PER_ENTRY).map(
      (isins, index) => ({
        id: `${group.key}:${index}`,
        query: securityQueryRequest({
          fields: group.fields,
          where: [
            { [qualifySecurityField(SECURITY_ID_FIELD)]: { in: isins } },
            ...group.where,
          ],
          requestParams: group.requestParams,
          limit: isins.length,
        }),
      }),
    ),
  )
}

interface BatchLine {
  id: string
  response?: QueryResponse
  error?: { code?: string; message?: string }
}

export interface SecurityFetchOptions {
  queryName: string
  signal?: AbortSignal
}

export class SecurityBatchQueryError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'SecurityBatchQueryError'
  }
}

// Each entry's response or error by entry id; rejects if an entry is missing.
async function executeBatchResults(
  entries: BatchQueryEntry[],
  options: SecurityFetchOptions,
): Promise<Map<string, QueryResponse | SecurityBatchQueryError>> {
  const response = await executeSormBatchQuery(
    { queries: entries },
    { queryName: options.queryName, signal: options.signal },
  )
  const resultsById = new Map<string, QueryResponse | SecurityBatchQueryError>()
  const body = await response.text()
  for (const text of body.split(/\r?\n/)) {
    if (text.trim().length === 0) continue
    const line = JSON.parse(text) as BatchLine
    resultsById.set(
      line.id,
      line.response && !line.error
        ? line.response
        : new SecurityBatchQueryError(
            `Security batch entry ${line.id} failed: ${line.error?.code ?? 'no response'} ${line.error?.message ?? ''}`.trim(),
          ),
    )
  }
  const missing = entries.filter((entry) => !resultsById.has(entry.id))
  if (missing.length > 0) {
    throw new SecurityBatchQueryError(
      `Security batch response is missing entries: ${missing.map((entry) => entry.id).join(', ')}`,
    )
  }
  return resultsById
}

// Rows by entry id; rejects if any entry fails or is missing from the response.
async function executeBatch(
  entries: BatchQueryEntry[],
  options: SecurityFetchOptions,
): Promise<Map<string, unknown[]>> {
  const results = await executeBatchResults(entries, options)
  const rowsById = new Map<string, unknown[]>()
  for (const [id, result] of results) {
    if (result instanceof SecurityBatchQueryError) throw result
    rowsById.set(id, result.data ?? [])
  }
  return rowsById
}

/** The ISINs whose order-book quotes to read, and the risk group pricing them. */
interface QuoteBookRequest {
  isins: ReadonlyArray<string>
  riskGroupId: string
}

/** The ISINs whose filter-list membership to read, against the org's lists. */
interface FilterListMembershipRequest {
  isins: ReadonlyArray<string>
  listIds: ReadonlyArray<string>
}

export interface SecurityBatchExtras {
  books?: QuoteBookRequest
  filterLists?: FilterListMembershipRequest
}

export interface SecuritiesWithBooks {
  rowsByKey: Map<string, SecurityRow[]>
  /** Order-book depth by ISIN; an ISIN with no merge-eligible quotes is absent. */
  books: Map<string, BondBook>
  /** List IDs holding each ISIN, in list order; empty without `filterLists`. */
  filterListsByIsin: Map<string, string[]>
}

function quoteEntries(
  chunks: ReadonlyArray<ReadonlyArray<string>>,
  riskGroupId: string,
): BatchQueryEntry[] {
  return chunks.map((isins, index) =>
    quoteBookBatchEntry(`${QUOTE_ENTRY_PREFIX}:${index}`, isins, riskGroupId),
  )
}

function quotePage(
  rowsById: ReadonlyMap<string, unknown[]>,
  index: number,
): QuoteRow[] {
  return (rowsById.get(`${QUOTE_ENTRY_PREFIX}:${index}`) ?? []) as QuoteRow[]
}

// The chunks with every full (possibly truncated) Quote page split in half,
// or null when no page filled up.
function splitFullQuoteChunks(
  chunks: ReadonlyArray<ReadonlyArray<string>>,
  rowsById: ReadonlyMap<string, unknown[]>,
): string[][] | null {
  let didSplit = false
  const next = chunks.flatMap((isins, index) => {
    if (!isQuotePageFull(quotePage(rowsById, index))) return [[...isins]]
    if (isins.length === 1) {
      throw new SecurityBatchQueryError(
        `Quote book for ${isins[0]} fills a whole page`,
      )
    }
    didSplit = true
    const half = Math.ceil(isins.length / 2)
    return [isins.slice(0, half), isins.slice(half)]
  })
  return didSplit ? next : null
}

// A full Quote page re-issues the WHOLE batch with smaller Quote entries, so
// the Security rows and every book still share one order-book snapshot.
async function fetchSnapshot(
  securityEntries: BatchQueryEntry[],
  chunks: ReadonlyArray<ReadonlyArray<string>>,
  riskGroupId: string,
  options: SecurityFetchOptions,
): Promise<{
  rowsById: Map<string, unknown[]>
  chunks: ReadonlyArray<ReadonlyArray<string>>
}> {
  const rowsById = await executeBatch(
    [...securityEntries, ...quoteEntries(chunks, riskGroupId)],
    options,
  )
  const resplit = splitFullQuoteChunks(chunks, rowsById)
  if (!resplit) return { rowsById, chunks }
  logger.warn('Quote page full; re-reading the whole Security batch', {
    quoteEntries: resplit.length,
  })
  return fetchSnapshot(securityEntries, resplit, riskGroupId, options)
}

/**
 * Fetches every group's Security rows, plus the order-book quotes and
 * filter-list membership `extras` asks for, in ONE batch-query, so all of them
 * read the same snapshot. Rejects if any entry fails or is missing.
 */
export async function fetchSecuritiesWithBooks(
  groups: ReadonlyArray<SecurityIsinGroup>,
  extras: SecurityBatchExtras,
  options: SecurityFetchOptions,
): Promise<SecuritiesWithBooks> {
  const rowsByKey = new Map<string, SecurityRow[]>(
    groups.map((group) => [group.key, []]),
  )
  const securityEntries = securityIsinBatchEntries(groups)
  const membership = planFilterListMembership(
    extras.filterLists?.isins ?? [],
    extras.filterLists?.listIds ?? [],
  )
  const bookChunks = extras.books ? quoteIsinChunks(extras.books.isins) : []
  if (securityEntries.length === 0 && bookChunks.length === 0) {
    return { rowsByKey, books: new Map(), filterListsByIsin: new Map() }
  }

  const { rowsById, chunks } = await fetchSnapshot(
    [...securityEntries, ...membership.entries],
    bookChunks,
    extras.books?.riskGroupId ?? '',
    options,
  )
  for (const entry of securityEntries) {
    const key = entry.id.slice(0, entry.id.lastIndexOf(':'))
    rowsByKey
      .get(key)
      ?.push(...((rowsById.get(entry.id) ?? []) as SecurityRow[]))
  }
  const quoteRows = chunks.flatMap((_, index) => quotePage(rowsById, index))
  return {
    rowsByKey,
    books: buildBooks(quoteRows),
    filterListsByIsin: membership.read(rowsById),
  }
}

/**
 * The list IDs holding each ISIN, in list order, read in a batch of its own.
 * For reads whose ISINs are known only after the row read, such as a page.
 */
export async function fetchFilterListMembership(
  request: FilterListMembershipRequest,
  options: SecurityFetchOptions,
): Promise<Map<string, string[]>> {
  const membership = planFilterListMembership(request.isins, request.listIds)
  if (membership.entries.length === 0) return new Map()
  return membership.read(await executeBatch(membership.entries, options))
}

export interface SecurityPage {
  rows: SecurityRow[]
  totalCount: number
}

/** One filtered, sorted page of Security rows. */
export async function fetchSecurityPage(
  input: SecurityQueryInput,
  options: SecurityFetchOptions,
): Promise<SecurityPage> {
  const response = await executeSormQuery(securityQueryRequest(input), {
    queryName: options.queryName,
    signal: options.signal,
  })
  return {
    rows: (response.data.data ?? []) as SecurityRow[],
    totalCount: response.data.metadata.total_count,
  }
}

/**
 * Several filtered pages of Security rows, keyed like `inputs`, in ONE
 * batch-query so they all read the same snapshot. An entry that fails maps to
 * its error without failing its siblings.
 */
export async function fetchSecurityPages(
  inputs: ReadonlyMap<string, SecurityQueryInput>,
  options: SecurityFetchOptions,
): Promise<Map<string, SecurityPage | SecurityBatchQueryError>> {
  const results = await executeBatchResults(
    [...inputs].map(([id, input]) => ({
      id,
      query: securityQueryRequest(input),
    })),
    options,
  )
  return new Map(
    [...results].map(([id, result]) => [
      id,
      result instanceof SecurityBatchQueryError
        ? result
        : {
            rows: (result.data ?? []) as SecurityRow[],
            totalCount: result.metadata.total_count,
          },
    ]),
  )
}
